/** ChatGPT-style markdown renderer (no deps).
 * Supports: headings (h1-h6), bold, italic, strikethrough, inline code,
 * fenced code blocks (with copy button), unordered/ordered lists (nested),
 * blockquotes, links, GFM tables, horizontal rules, inline citations [1].
 */
import { useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { Copy, Check } from "lucide-react";

const SANITIZE_OPTS = {
  USE_PROFILES: { html: true },
  ADD_ATTR: ["target", "rel"],
  FORBID_TAGS: ["style", "iframe", "object", "embed", "form", "input"],
};
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("href")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

function escapeHtml(s) {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

// Anchor schemes that are safe to render. Anything else (`javascript:`,
// `data:`, `vbscript:`, etc.) is rewritten to `#` so a malicious LLM /
// markdown payload like `[click me](javascript:alert(1))` cannot fire script.
function safeUrl(url) {
  const trimmed = (url || "").trim();
  if (!trimmed) return "#";
  // Relative, hash, mailto/tel, http(s) are fine.
  if (/^(https?:|mailto:|tel:|#|\/|\.\/|\.\.\/)/i.test(trimmed)) return trimmed;
  // Anything with a colon before a slash is treated as a scheme — block it.
  if (/^[a-z][a-z0-9+.\-]*:/i.test(trimmed)) return "#";
  return trimmed;
}

function renderInline(text) {
  let out = escapeHtml(text);
  // links [text](url) — sanitize url scheme before interpolation
  out = out.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_m, label, url) => `<a href="${safeUrl(url)}" target="_blank" rel="noopener noreferrer">${label}</a>`
  );
  // bold
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  // strikethrough
  out = out.replace(/~~([^~]+)~~/g, "<del>$1</del>");
  // italic (avoid hitting inside ** already-rendered or middle of word)
  out = out.replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s).,!?]|$)/g, "$1<em>$2</em>");
  out = out.replace(/(^|[\s(])_([^_\n]+)_(?=[\s).,!?]|$)/g, "$1<em>$2</em>");
  // inline code
  out = out.replace(/`([^`\n]+)`/g, "<code>$1</code>");
  // citations like [1] → styled chip (only single digits at word boundary)
  out = out.replace(
    /(?<!\w)\[(\d{1,2})\](?!\()/g,
    '<sup class="md-cite">[$1]</sup>'
  );
  return out;
}

function parseTable(lines, startIdx) {
  // Returns { html, nextIdx } if a table starts at startIdx, else null
  const headerLine = lines[startIdx];
  const separatorLine = lines[startIdx + 1];
  if (!headerLine || !separatorLine) return null;
  if (!/^\s*\|.+\|\s*$/.test(headerLine)) return null;
  if (!/^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(separatorLine)) return null;

  const splitRow = (l) =>
    l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());

  const headers = splitRow(headerLine);
  const aligns = splitRow(separatorLine).map((s) => {
    const left = s.startsWith(":");
    const right = s.endsWith(":");
    if (left && right) return "center";
    if (right) return "right";
    return "left";
  });

  const rows = [];
  let i = startIdx + 2;
  while (i < lines.length && /^\s*\|.+\|\s*$/.test(lines[i])) {
    rows.push(splitRow(lines[i]));
    i++;
  }

  let html = '<div class="md-table-wrap"><table>';
  html += "<thead><tr>";
  headers.forEach((h, k) => {
    html += `<th style="text-align:${aligns[k] || "left"}">${renderInline(h)}</th>`;
  });
  html += "</tr></thead><tbody>";
  rows.forEach((r) => {
    html += "<tr>";
    r.forEach((c, k) => {
      html += `<td style="text-align:${aligns[k] || "left"}">${renderInline(c)}</td>`;
    });
    html += "</tr>";
  });
  html += "</tbody></table></div>";
  return { html, nextIdx: i };
}

function buildHtml(content) {
  if (!content) return { blocks: [] };
  const lines = content.split("\n");
  const blocks = []; // Array of { type: 'html' | 'code', ...}
  let buffer = "";
  let inCode = false;
  let codeLang = "";
  let codeBuf = [];
  let listType = null;
  let listStack = [];

  const flushList = () => {
    while (listStack.length) {
      buffer += `</${listStack.pop()}>`;
    }
    listType = null;
  };

  const flushBuffer = () => {
    if (buffer) {
      blocks.push({ type: "html", html: buffer });
      buffer = "";
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Fenced code blocks
    if (line.startsWith("```")) {
      if (inCode) {
        flushBuffer();
        blocks.push({ type: "code", lang: codeLang, code: codeBuf.join("\n") });
        inCode = false;
        codeBuf = [];
        codeLang = "";
      } else {
        flushList();
        inCode = true;
        codeLang = line.slice(3).trim();
      }
      continue;
    }
    if (inCode) {
      codeBuf.push(line);
      continue;
    }

    // Horizontal rule
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      flushList();
      buffer += "<hr/>";
      continue;
    }

    // Tables (try to detect)
    if (/^\s*\|.+\|\s*$/.test(line)) {
      const t = parseTable(lines, i);
      if (t) {
        flushList();
        buffer += t.html;
        i = t.nextIdx - 1;
        continue;
      }
    }

    // Headings h1-h6
    const hMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (hMatch) {
      flushList();
      const lvl = hMatch[1].length;
      buffer += `<h${lvl}>${renderInline(hMatch[2])}</h${lvl}>`;
      continue;
    }

    // Lists (ul/ol with indent)
    const ulMatch = line.match(/^(\s*)[-*+]\s+(.*)$/);
    const olMatch = line.match(/^(\s*)(\d+)\.\s+(.*)$/);
    if (ulMatch || olMatch) {
      const type = ulMatch ? "ul" : "ol";
      const content = ulMatch ? ulMatch[2] : olMatch[3];
      if (listType !== type) {
        if (listStack.length === 0) {
          buffer += `<${type}>`;
          listStack.push(type);
          listType = type;
        } else {
          // top-level swap
          buffer += `</${listStack.pop()}><${type}>`;
          listStack.push(type);
          listType = type;
        }
      }
      buffer += `<li>${renderInline(content)}</li>`;
      continue;
    }

    if (line.startsWith("> ")) {
      flushList();
      buffer += `<blockquote>${renderInline(line.slice(2))}</blockquote>`;
      continue;
    }

    if (line.trim() === "") {
      flushList();
      buffer += "";
      continue;
    }

    flushList();
    buffer += `<p>${renderInline(line)}</p>`;
  }
  flushList();
  if (inCode) {
    flushBuffer();
    blocks.push({ type: "code", lang: codeLang, code: codeBuf.join("\n") });
  }
  flushBuffer();
  return { blocks };
}

function CodeBlock({ lang, code }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* noop */ }
  };
  return (
    <div className="md-code">
      <div className="md-code-head">
        <span className="md-code-lang">{lang || "text"}</span>
        <button
          type="button"
          onClick={onCopy}
          className="md-code-copy"
          data-testid="md-code-copy"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre><code className={`lang-${lang || "text"}`}>{code}</code></pre>
    </div>
  );
}

export function Markdown({ content }) {
  const { blocks } = useMemo(() => buildHtml(content), [content]);
  return (
    <div className="markdown-content">
      {blocks.map((b, i) =>
        b.type === "code" ? (
          <CodeBlock key={`code-${i}-${b.lang || ""}-${b.code.length}`} lang={b.lang} code={b.code} />
        ) : (
          <div
            key={`html-${i}-${b.html.length}`}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(b.html, SANITIZE_OPTS) }}
          />
        )
      )}
    </div>
  );
}
